import { getErrorMessage } from '@features/shared/api/client'
import { sidebarQueryKeys } from '@features/sidebar/api/queryKeys'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { saveTableOrganization } from '../api/tableOrganization.api'
import { useToast } from '../../shared/components/Toast/ToastContext'
import type {
  SaveTableSidebarOrganizationResponse,
  TableSidebarOrganizationResponse,
  TableSidebarTree,
} from '../api/contracts'

interface SaveContext {
  previous: TableSidebarOrganizationResponse | undefined
  saveId: number
}

/** Saves reach the server one at a time, in the order they were made. */
const SAVE_SCOPE = { id: 'table-sidebar-organization' }

// The newest save, across every caller (the list, the Add folder button). An
// older save settling never overwrites what a newer one already shows.
let latestSaveId = 0

/**
 * Saves the entire folder structure in one PUT.
 *
 * Optimistically applies the reorder to the cache (so the sidebar updates
 * instantly), rolls back to the pre-drag tree if the save fails, and seeds the
 * cache with the server's response on success. In-flight reads are cancelled so
 * a slow refetch can't clobber the optimistic state. Only the newest save
 * rolls back or seeds; each save carries the whole tree, so the newest one
 * also carries everything before it.
 */
export function useSaveTableOrganization() {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  return useMutation<SaveTableSidebarOrganizationResponse, Error, TableSidebarTree, SaveContext>({
    scope: SAVE_SCOPE,
    mutationFn: (organization) => saveTableOrganization(organization),
    onMutate: async (organization) => {
      const saveId = ++latestSaveId
      await queryClient.cancelQueries({ queryKey: sidebarQueryKeys.tableOrganization })
      const previous = queryClient.getQueryData<TableSidebarOrganizationResponse>(
        sidebarQueryKeys.tableOrganization
      )
      queryClient.setQueryData<TableSidebarOrganizationResponse>(
        sidebarQueryKeys.tableOrganization,
        {
          tree: organization,
          tables: previous?.tables ?? [],
          updated_at: previous?.updated_at ?? null,
        }
      )
      return { previous, saveId }
    },
    onError: (error, _organization, context) => {
      // Revert to the tree as it was before the drag and surface the failure.
      if (context?.saveId === latestSaveId && context.previous !== undefined) {
        queryClient.setQueryData(sidebarQueryKeys.tableOrganization, context.previous)
      }
      addToast({
        type: 'error',
        title: "Couldn't save table organization",
        message: getErrorMessage(error),
      })
    },
    onSuccess: (response, _organization, context) => {
      if (context?.saveId !== latestSaveId) return
      // Seed the cache with the saved tree (avoids a refetch round-trip). The
      // table list is today's, so opens recorded during the save stay.
      queryClient.setQueryData<TableSidebarOrganizationResponse>(
        sidebarQueryKeys.tableOrganization,
        (current) => ({
          tree: response.tree,
          tables: current?.tables ?? context.previous?.tables ?? [],
          updated_at: response.updated_at,
        })
      )
    },
  })
}
